"use client";
import { formatCurrency } from "@/utils/currencyFormatter";
import clsx from "clsx";
import { memo, useMemo } from "react";

/**
 * Takvim grid'indeki sütuna göre tooltip yatay hizası (haftanın günü değil).
 * Kenar sütunlarda center + geniş w-32 taşmasını önler.
 * @param {Date} date
 * @param {0|1|2|3|4|5|6} weekStartsOn — DayPicker locale ile aynı olmalı
 */
const getTooltipHorizontalPosition = (date, weekStartsOn) => {
  const d = new Date(date);
  const jsDay = d.getDay();
  const columnIndex = (jsDay - weekStartsOn + 7) % 7;

  if (columnIndex <= 1) return "left";
  if (columnIndex >= 5) return "right";
  return "center";
};

const PriceDayButton = memo(
  ({
    day,
    children,
    getPriceByDate,
    isOneOfCheapestDay,
    currency,
    isHoliday = false,
    showReturnToolTip = false,
    weekStartsOn = 1,
    ...buttonProps
  }) => {
    const price = useMemo(
      () => getPriceByDate(day.date),
      [getPriceByDate, day.date],
    );

    const isCheapest = useMemo(
      () => isOneOfCheapestDay(day.date) || false,
      [isOneOfCheapestDay, day.date],
    );

    const formattedPrice = useMemo(
      () =>
        price
          ? formatCurrency(price, currency, {
              showSymbol: true,
              showCode: false,
            })
          : null,
      [price, currency],
    );

    const position = useMemo(
      () => getTooltipHorizontalPosition(day.date, weekStartsOn),
      [day.date, weekStartsOn],
    );

    const isSelected = buttonProps?.modifiers?.selected;
    return (
      <button
        {...buttonProps}
        className="relative !mx-auto flex h-full !w-full cursor-pointer flex-col items-center justify-center"
      >
        {showReturnToolTip && isSelected && (
          <div
            className={clsx(
              "bg-foreground-muted absolute bottom-full z-[1] mb-2.5 w-32 transform rounded-sm text-sm font-normal text-white",
              position === "left" && "left-1",
              position === "right" && "right-1",
              position === "center" && "left-1/2 -ml-16",
            )}
          >
            <div className="relative w-full px-3 py-2">
              {showReturnToolTip}
              <span
                className={clsx(
                  "bg-foreground-muted absolute top-full -mt-1 h-2 w-2 rotate-45 transform",
                  position === "left" && "left-[22px]",
                  position === "right" && "right-[22px]",
                  position === "center" && "left-1/2 -ml-1",
                )}
              ></span>
            </div>
          </div>
        )}
        {isHoliday && (
          <div className="bg-tertiary-emphasis absolute top-[2px] right-[2px] h-[6px] w-[6px] rounded-full"></div>
        )}
        <div
          className={clsx(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-semibold",
            isSelected && !buttonProps?.modifiers?.range_middle
              ? "bg-primary rounded-full text-white"
              : "",
            buttonProps?.modifiers?.range_middle && isSelected
              ? "text-primary rounded-none"
              : "",
          )}
        >
          <span>{children}</span>
        </div>
        <span
          className={clsx(
            "text-foreground-muted h-full w-full flex-1 bg-white pt-[3px] text-xs leading-none font-thin",
            isCheapest && "text-teal",
          )}
        >
          {formattedPrice}
        </span>
      </button>
    );
  },
);

PriceDayButton.displayName = "PriceDayButton";

export default PriceDayButton;
